import React, { useState } from "react";
import { Navigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import { PageLoader } from "@/components/common/PageLoader";
import { sharedAssignmentsAPI } from "@/lib/api/sharedAssignments";
import { clearShareRedirectToken, getShareRedirectToken } from "@/lib/shareLink";

export const SHARE_LINK_EXPIRED = "This link has expired";
export const SHARE_LINK_FAILED = "Couldn't open the shared assignment";

const responseStatus = (err: unknown): number | undefined =>
  axios.isAxiosError(err)
    ? err.response?.status
    : (err as { response?: { status?: number } })?.response?.status;

interface PostLoginRedirectProps {
  /** Where a signed-in user goes when there is no shared assignment to open. */
  fallback: string;
}

/**
 * Where the login page sends a signed-in user (#1072). A visitor who came
 * through the website's "Log in" link on a shared assignment
 * (`/login?share=<token>`) lands on that assignment; an expired or unknown
 * link says so and continues to the normal landing page. Also covers a
 * visitor who is already signed in: the session restores on the login page,
 * which then renders this.
 */
export const PostLoginRedirect: React.FC<PostLoginRedirectProps> = ({ fallback }) => {
  // Read once: the redirect is cleared as soon as it has been used.
  const [token] = useState(getShareRedirectToken);
  if (!token) {
    return <Navigate to={fallback} replace />;
  }
  return <OpenSharedAssignment token={token} fallback={fallback} />;
};

/**
 * The assignment behind the link, or null when it can't be opened. Settles
 * the one-shot redirect inside the query, so the toast shows once (the query
 * is shared, also under StrictMode) and rendering stays free of effects.
 */
const openSharedAssignment = async (token: string): Promise<number | null> => {
  try {
    const { assignment_id } = await sharedAssignmentsAPI.resolve(token);
    return assignment_id;
  } catch (err) {
    const status = responseStatus(err);
    if (status === 404 || status === 410) {
      toast.info(SHARE_LINK_EXPIRED);
    } else {
      toast.error(SHARE_LINK_FAILED);
    }
    return null;
  } finally {
    clearShareRedirectToken();
  }
};

const OpenSharedAssignment: React.FC<{ token: string; fallback: string }> = ({
  token,
  fallback,
}) => {
  const resolved = useQuery({
    queryKey: ["shared-assignment-resolve", token],
    queryFn: () => openSharedAssignment(token),
    retry: false,
    gcTime: 0,
  });
  if (resolved.isPending) {
    return <PageLoader fullScreen />;
  }
  const assignmentId = resolved.data;
  return (
    <Navigate
      to={typeof assignmentId === "number" ? `/assignments/${assignmentId}` : fallback}
      replace
    />
  );
};
